import React, { useRef } from "react";
import styled from "styled-components";
import { View } from "vega";

import { useCompositeRef } from "../../hooks/useCompositeRef";
import { useResizeSensor } from "../../hooks/useResizeSensor";

export interface HorizontalLegendContainerProps {
  view: View | null;
  resizeChart: (view: View | null, heightDelta: number) => void;
  children: JSX.Element;
}

/**
 * The horizontal legend container is special compared to the vertical
 * legend container. This is because horizontal legend and chart are
 * stacked on top of each other which means that when the legend grows
 * in height (via the show more/collapse action), the chart needs to
 * change its height to accommodate the legend.
 *
 * When the chart is growing to take up the space that the legend used
 * to occupy, the chart is able to resize itself via flex-grow (the div
 * containing the chart grows and the chart detects the resize and resizes
 * itself). However when the legend grows and needs to take up the space
 * that the chart occupies, the chart doesn't shrink by itself as it has
 * a fixed height and the containing div doesn't shrink. To account for
 * this we need to detect when the legend grows and manually resize the
 * chart.
 */
export const HorizontalLegendContainer: React.ComponentType<HorizontalLegendContainerProps> =
  React.memo(function HorizontalLegendContainer({
    children,
    resizeChart,
    view,
  }: HorizontalLegendContainerProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const prevHeight = useRef<number>(0);
    if (prevHeight.current === 0 && containerRef.current != null) {
      prevHeight.current = containerRef.current.clientHeight;
    }

    const legendResizeRef = useResizeSensor({
      onResize: () => {
        if (containerRef.current != null) {
          const { clientHeight } = containerRef.current;
          // we only trigger a resize if the legend is growing as css
          // and the resize sensor on the chart container will handle
          // the chart growing (see comment above)
          if (clientHeight > prevHeight.current) {
            resizeChart(view, prevHeight.current - clientHeight);
          }
          prevHeight.current = clientHeight;
        }
      },
    });
    const ref = useCompositeRef(containerRef, legendResizeRef);

    return <Container ref={ref}>{children}</Container>;
  });

const Container = styled.div`
  z-index: 1;

  flex-shrink: 0;
  max-height: 92px;
  margin-bottom: 8px;
  margin-left: 27px;
`;
